import React, { useState, useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import ProjectList from "./ProjectList";
import Icon from "./Icons";
import "../styles/Projects.css";
import { BsBarChartFill, BsImage } from "react-icons/bs";
import { FaExternalLinkAlt } from "react-icons/fa";

gsap.registerPlugin(ScrollTrigger);

const projectsData = ProjectList;
const categories = Object.keys(projectsData);

const openImageInNewTab = (imageUrl) => {
  window.open(imageUrl, '_blank', 'noopener,noreferrer');
};

export default function Projects() {
  const [selectedProject, setSelectedProject] = useState({
    category: categories[0],
    projectIdx: 0,
  });
  const [dashboardView, setDashboardView] = useState({});
  const [iframeLoading, setIframeLoading] = useState(true);

  const projectsRef = useRef(null);
  const contentRef = useRef(null);

  const { category: activeCategory, projectIdx: activeProjectIdx } = selectedProject;
  const currentCategoryProjects = projectsData[activeCategory] || projectsData[categories[0]] || [];
  const activeProject = currentCategoryProjects[activeProjectIdx] || currentCategoryProjects[0] || {};

  useEffect(() => {
    gsap.fromTo("#projects .section-title",
      { y: 30, opacity: 0 },
      {
        y: 0, opacity: 1, duration: 0.6, ease: "power2.out",
        scrollTrigger: {
          trigger: "#projects",
          start: "top 80%",
          toggleActions: "play none none reverse"
        }
      }
    );

    gsap.fromTo(".projects-main-container",
      { y: 80, opacity: 0 },
      {
        y: 0, opacity: 1, duration: 0.8, ease: "power3.out",
        scrollTrigger: {
          trigger: ".projects-main-container",
          start: "top 85%",
          toggleActions: "play none none reverse"
        }
      }
    );

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  const handleProjectSelect = (categoryKey, projectIdx) => {
    if (activeCategory === categoryKey && activeProjectIdx === projectIdx) {
      return;
    }

    const contentElement = contentRef.current;

    if (contentElement) {
      gsap.killTweensOf(contentElement);
      gsap.to(contentElement, {
        opacity: 0,
        y: -10,
        duration: 0.18,
        ease: "power2.in",
        onComplete: () => {
          setSelectedProject({ category: categoryKey, projectIdx });
          setIframeLoading(true);

          gsap.fromTo(
            contentElement,
            { y: 15, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              duration: 0.35,
              ease: "power2.out",
              clearProps: "all",
            }
          );
        },
      });
    } else {
      setSelectedProject({ category: categoryKey, projectIdx });
      setIframeLoading(true);
    }
  };

  return (
    <section id="projects" ref={projectsRef}>
      <div className="section-header">
        <span className="section-title">Projects</span>
      </div>

      <div className="projects-main-container">
        <div className="directory-box gradient-border">
          {/* Sidebar */}
          <div className="directory-sidebar">
            {categories.map((categoryKey) => (
              <div key={categoryKey} className="category-group">
                <div className="category-header">
                  <span className="category-label">{categoryKey}</span>
                </div>
                <ul className="category-items">
                  {projectsData[categoryKey].map((project, idx) => {
                    const isActive = activeCategory === categoryKey && activeProjectIdx === idx;
                    return (
                      <li key={idx}>
                        <button
                          className={`project-name-btn${isActive ? " active" : ""}`}
                          onClick={() => handleProjectSelect(categoryKey, idx)}
                        >
                          <span className="project-dot" />
                          {project.name}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>

          {/* Content Panel */}
          <div className="directory-content" ref={contentRef}>
            <div className="project-info-container">
              {/* Image / Power BI Embed */}
              <div className="project-image-container">
                {activeProject?.powerbi ? (
                  dashboardView[`${activeCategory}-${activeProjectIdx}`] !== false ? (
                    <div className="powerbi-embed-wrapper" key={`${activeProject.name || 'dashboard'}-wrapper`}>
                      {iframeLoading && (
                        <div className="powerbi-loader">
                          <div className="powerbi-spinner"></div>
                          <span>Loading {activeProject.name || "dashboard"}...</span>
                        </div>
                      )}
                      <iframe
                        key={activeProject.powerbi}
                        title={activeProject.name || "PowerBI Dashboard"}
                        src={activeProject.powerbi}
                        frameBorder="0"
                        allowFullScreen={true}
                        className="powerbi-iframe"
                        onLoad={() => setIframeLoading(false)}
                      />
                    </div>
                  ) : (
                    <div className="image-wrapper">
                      <img src={activeProject.image} alt={activeProject.name || "Dashboard Preview"} loading="lazy" decoding="async" />
                    </div>
                  )
                ) : (
                  <div className="image-wrapper">
                    {activeProject?.image && (
                      <img src={activeProject.image} alt={activeProject.name || "Project Preview"} loading="lazy" decoding="async" />
                    )}
                  </div>
                )}
              </div>

              {/* Project Header: Title on the left, Controls right-aligned */}
              <div className="project-header-row">
                <h3 className="project-title">{activeProject?.name}</h3>

                {activeProject?.powerbi && (
                  <div className="dashboard-toggle-bar">
                    <button
                      className={`dashboard-toggle-btn ${dashboardView[`${activeCategory}-${activeProjectIdx}`] !== false ? 'active' : ''}`}
                      onClick={() => setDashboardView(prev => ({ ...prev, [`${activeCategory}-${activeProjectIdx}`]: true }))}
                      title="Live Dashboard"
                    >
                      <BsBarChartFill /> <span>Live Dashboard</span>
                    </button>
                    <button
                      className={`dashboard-toggle-btn ${dashboardView[`${activeCategory}-${activeProjectIdx}`] === false ? 'active' : ''}`}
                      onClick={() => setDashboardView(prev => ({ ...prev, [`${activeCategory}-${activeProjectIdx}`]: false }))}
                      title="Screenshot"
                    >
                      <BsImage /> <span>Screenshot</span>
                    </button>
                    <a
                      href={activeProject.powerbi}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="dashboard-toggle-btn dashboard-external-link"
                      title="Open in Full Screen"
                    >
                      <FaExternalLinkAlt /> <span>Full Screen</span>
                    </a>
                  </div>
                )}
              </div>

              <p className="project-description">{activeProject?.description}</p>

              <ul className="project-tags">
                {activeProject?.tags && activeProject.tags.map((tag, i) => (
                  <li key={i} className="tag-pill">{tag}</li>
                ))}
              </ul>

              <div className="project-links">
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}